import {
  DATE_RANGE_OPERANDS,
  DROPDOWN_OPERANDS,
  INPUT_NUMBER_OPERANDS,
  INPUT_TEXT_OPERANDS,
} from '../../components/DataGrid/constants/filters';
import type { ColumnFilter } from '../../components/DataGrid/types/filters';
import { ColumnFilterType } from '../../components/DataGrid/types/filters';
import { COUNTRIES } from '../../constants/countries';
import { columnDefs } from './columnsDefs';

export interface DropdownOption {
  value: string;
  label: string;
}

const setInputTextFilter = (
  column: ColumnFilter,
  opts?: Partial<ColumnFilter>,
): void => {
  column.type = ColumnFilterType.inputText;

  if (opts?.arrayField) {
    column.arrayField = opts.arrayField;
  }
};

const setInputNumberFilter = (column: ColumnFilter): void => {
  column.type = ColumnFilterType.inputNumber;
};

const setDropdownFilter = (
  column: ColumnFilter,
  opts?: Partial<ColumnFilter>,
): void => {
  column.type = ColumnFilterType.dropdown;

  if (opts?.arrayField) {
    column.arrayField = opts.arrayField;
  }

  if (opts?.multiValue) {
    column.multiValue = opts.multiValue;
  }

  if (opts?.customOptions) {
    column.customOptions = opts.customOptions;
  }
};

const setDateFilter = (column: ColumnFilter): void => {
  column.type = ColumnFilterType.dateRange;
};

const countryFieldOptions: DropdownOption[] = COUNTRIES.map((c) => ({
  value: c.code,
  label: c.name,
}));

// ToDo: Remove switch, refactor to model approach
export const filterColumns: ColumnFilter[] = columnDefs
  .filter((c) => {
    if (c.field?.startsWith('_')) {
      return false;
    } // Exclude ephemeral columns

    // Add here non-filterable columns
    return true;
  })
  .map((c) => {
    const filterColumn: ColumnFilter = {
      ...c,
      operands: [],
      field: c.field!,
      name: (c.filterName ?? c.headerName)!,
      type: ColumnFilterType.inputText,
      arrayField: false,
      multiValue: false,
    };

    switch (c.field) {
      case 'wmg1Labels':
        filterColumn.type = ColumnFilterType.wmg1Dropdown;
        break;
      case 'artist':
      case 'trackTitle':
      case 'isrc':
      case 'shortTitle':
      case 'recordingVersion':
      case 'paFlag':
      case 'audioPresentation':
        setInputTextFilter(filterColumn);
        break;
      case 'albumTitle':
      case 'albumSubtitle':
      case 'gpid':
      case 'grid':
        setInputTextFilter(filterColumn, { arrayField: true });
        break;
      case 'majorLabel':
      case 'releaseType':
      case 'partnerList':
      case 'presentationLabel':
        setDropdownFilter(filterColumn, { arrayField: true, multiValue: true });
        break;
      case 'trackRightsTerritories':
      case 'deliveryTerritoryList':
      case 'appleTerritoryList':
      case 'spotifyTerritoryList':
      case 'missedTerritoriesAlt':
      case 'missedAppleTerritoriesAlt':
      case 'missedSpotifyTerritoriesAlt':
      case 'monthlyTrendFixesAddedTerritoryList':
        setDropdownFilter(filterColumn, {
          arrayField: true,
          multiValue: true,
          customOptions: countryFieldOptions,
        });
        break;
      case 'explicit':
      case 'trackRightType':
      case 'recType':
      case 'recSubType':
      case 'repertoireOwner':
        setDropdownFilter(filterColumn, { multiValue: true });
        break;
      case 'optTerritory':
      case 'deliveryTerritoryCount':
      case 'appleTerritoryCount':
      case 'spotifyTerritoryCount':
      case 'partnerCount':
      case 'missedTerritoriesCountAlt':
      case 'missedAppleTerritoriesCountAlt':
      case 'missedSpotifyTerritoriesCountAlt':
      case 'trackRightsTerritoryCount':
      case 'monthlyTrendFixesAddedRevenue':
      case 'monthlyTrendFixesAddedStreams':
      case 'momPctRevenueNet':
      case 'lmrNetValue':
      case 'llmrNetValue':
      case 'revenueYtdUsdNet':
      case 'monthlyTrendRevenueNet':
      case 'monthlyTrendFixesAddedRevenueNet':
        setInputNumberFilter(filterColumn);
        break;
      case 'latestProductTs':
        setDateFilter(filterColumn);
        break;
      case undefined:
      default:
        setInputTextFilter(filterColumn);
        break;
    }

    switch (filterColumn.type) {
      case ColumnFilterType.inputText:
        filterColumn.operands = INPUT_TEXT_OPERANDS;
        break;
      case ColumnFilterType.inputNumber:
        filterColumn.operands = INPUT_NUMBER_OPERANDS;
        break;
      case ColumnFilterType.dropdown:
        filterColumn.operands = DROPDOWN_OPERANDS;
        break;
      case ColumnFilterType.dateRange:
        filterColumn.operands = DATE_RANGE_OPERANDS;
        break;
      case undefined:
      default:
        filterColumn.operands = INPUT_TEXT_OPERANDS;
        break;
    }

    return filterColumn;
  });
